import { useState } from "react";

type AspectRatio = "9:16" | "1:1" | "16:9";

export default function EditingToolsPanel() {
  const [faceTracking, setFaceTracking] = useState(true);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("9:16");
  const [autoVolume, setAutoVolume] = useState(true);
  const [removeSilence, setRemoveSilence] = useState(false);
  const [reframeX, setReframeX] = useState(50);
  const [reframeY, setReframeY] = useState(30);

  const Toggle = ({
    enabled,
    onToggle,
  }: {
    enabled: boolean;
    onToggle: () => void;
  }) => (
    <button
      onClick={onToggle}
      className={`relative w-9 h-5 rounded-full transition-all cursor-pointer shrink-0 ${
        enabled ? "bg-[#00D9FF]" : "bg-white/10"
      }`}
    >
      <div
        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
          enabled ? "left-5" : "left-0.5"
        }`}
      />
    </button>
  );

  const ASPECT_RATIOS: { value: AspectRatio; icon: string; label: string }[] = [
    { value: "9:16", icon: "ri-smartphone-line", label: "9:16" },
    { value: "1:1", icon: "ri-square-line", label: "1:1" },
    { value: "16:9", icon: "ri-tv-line", label: "16:9" },
  ];

  return (
    <div className="h-full flex flex-col gap-4 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
      {/* Panel header */}
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-white/8">
          <i className="ri-tools-line text-gray-300 text-sm" />
        </div>
        <span className="text-white text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          편집 도구
        </span>
      </div>

      {/* Crop & Reframe */}
      <div className="bg-[#0F1419] border border-white/8 rounded-xl p-4">
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          크롭 & 리프레임
        </p>

        {/* Face tracking toggle */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>얼굴 자동 추적</p>
            <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>AI가 주요 인물을 자동으로 프레임 중앙에 배치</p>
          </div>
          <Toggle enabled={faceTracking} onToggle={() => setFaceTracking(!faceTracking)} />
        </div>

        {/* Manual reposition (only if face tracking is off) */}
        {!faceTracking && (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-gray-400 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>수평 위치</span>
                <span className="text-[#00D9FF] text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{reframeX}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={reframeX}
                onChange={(e) => setReframeX(Number(e.target.value))}
                className="w-full accent-[#00D9FF] cursor-pointer"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-gray-400 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>수직 위치</span>
                <span className="text-[#00D9FF] text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{reframeY}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={reframeY}
                onChange={(e) => setReframeY(Number(e.target.value))}
                className="w-full accent-[#00D9FF] cursor-pointer"
              />
            </div>
          </div>
        )}

        {faceTracking && (
          <div className="flex items-center gap-2 bg-[#00D9FF]/8 border border-[#00D9FF]/20 rounded-lg px-3 py-2">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-focus-3-line text-[#00D9FF] text-xs" />
            </span>
            <span className="text-[#00D9FF] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              주요 인물 감지됨 · 자동 추적 중
            </span>
          </div>
        )}
      </div>

      {/* Aspect Ratio */}
      <div className="bg-[#0F1419] border border-white/8 rounded-xl p-4">
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          화면 비율
        </p>
        <div className="grid grid-cols-3 gap-2">
          {ASPECT_RATIOS.map((ar) => (
            <button
              key={ar.value}
              onClick={() => setAspectRatio(ar.value)}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-all cursor-pointer ${
                aspectRatio === ar.value
                  ? "border-[#00D9FF]/50 bg-[#00D9FF]/10 text-[#00D9FF]"
                  : "border-white/8 bg-white/3 text-gray-400 hover:border-white/15"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={ar.icon} />
              </span>
              <span className="text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {ar.label}
              </span>
              {ar.value === "9:16" && (
                <span className="text-xs opacity-70" style={{ fontFamily: "'Inter', sans-serif" }}>
                  기본
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Audio Settings */}
      <div className="bg-[#0F1419] border border-white/8 rounded-xl p-4">
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          오디오
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>자동 볼륨 최적화</p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>볼륨 편차를 자동으로 균일하게 조정</p>
            </div>
            <Toggle enabled={autoVolume} onToggle={() => setAutoVolume(!autoVolume)} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>무음 구간 자동 제거</p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>불필요한 침묵 구간을 자동으로 잘라냄</p>
            </div>
            <Toggle enabled={removeSilence} onToggle={() => setRemoveSilence(!removeSilence)} />
          </div>
        </div>

        {/* Audio waveform visualization */}
        <div className="mt-4 h-10 flex items-center gap-px">
          {Array.from({ length: 50 }, (_, i) => {
            const height = Math.sin(i * 0.4) * 0.4 + 0.5 + Math.random() * 0.2;
            return (
              <div
                key={i}
                className="flex-1 rounded-full"
                style={{
                  height: `${Math.min(100, Math.max(10, height * 100))}%`,
                  background: i > 10 && i < 20 && removeSilence
                    ? "rgba(239,68,68,0.2)"
                    : "rgba(0,217,255,0.3)",
                }}
              />
            );
          })}
        </div>
        {removeSilence && (
          <p className="text-xs text-[#EF4444] mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            빨간 구간: 무음 제거 예정
          </p>
        )}
      </div>
    </div>
  );
}
